import { FC, ComponentProps } from 'react';
import styled from 'styled-components';
import { Colors, Sizes } from '@altermaind/web/ui';
import { convertSizeToNumber } from '@/_shared/helpers';
import { convertColorToRgba } from '@/_shared/helpers/convertColorToRgba';
import { WrappedInput } from '../WrappedInput';

type WrapperProps = {
  px?: keyof Sizes;
  sliderHeight?: keyof Sizes;
  circleSize?: keyof Sizes;
  trackColor?: keyof Colors;
};

export const RangeWrapper = styled.div<WrapperProps>`
  /* vars comuni ereditabili dall'input */
  --padding: ${({ theme, px = 'xs' }) => `${convertSizeToNumber(theme?.sizes?.[px] || 8)}px`};
  --track-height: ${({ theme, sliderHeight = 'xs' }) =>
    `${convertSizeToNumber(theme?.sizes?.[sliderHeight] || 12)}px`};
  --thumb-size: ${({ theme, circleSize = 'm' }) =>
    `${convertSizeToNumber(theme?.sizes?.[circleSize] || 16)}px`};
  --track-color: ${({ theme, trackColor }) =>
    (trackColor && (theme.colors as Colors)[trackColor]) ||
    (theme.colors as Colors).white ||
    'white'};

  position: relative;
  padding: 0 var(--padding);
  height: var(--thumb-size);

  /* barra bianca disegnata dal wrapper, centrata verticalmente */
  &::before {
    content: '';
    position: absolute;
    left: 0; /* rispetta il padding del wrapper */
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    height: var(--track-height);
    background: var(--track-color);
    border-radius: 9999px;
    pointer-events: none; /* non blocca il drag */
    margin-top: 1px;
  }
`;

type Props = ComponentProps<typeof WrappedInput> & {
  circleSize?: keyof Sizes;
  circleColor?: keyof Colors;
  sliderHeight?: keyof Sizes;
};

export const StyledRangeInput = styled(WrappedInput).attrs({ type: 'range' })<Props>`
  --thumb-size: ${({ theme, circleSize = 'm' }) =>
    `${convertSizeToNumber(theme?.sizes?.[circleSize] || 16)}px`};
  --track-height: ${({ theme, sliderHeight = 'xs' }) =>
    `${convertSizeToNumber(theme?.sizes?.[sliderHeight] || 12)}px`};
  --thumb-color: ${({ theme, circleColor = 'primary' }) =>
    convertColorToRgba((theme.colors as Colors)[circleColor] || '#197EF7', 1)};
  --thumb-shadow: ${({ theme, circleColor = 'primary' }) =>
    convertColorToRgba((theme.colors as Colors)[circleColor] || '#197EF7', 0.2)};

  appearance: none;
  width: 100%;
  height: var(--thumb-size); /* <-- per non tagliare lo shadow */
  background: transparent; /* <-- il binario lo disegna il wrapper */
  border-radius: 9999px;
  padding: 0;
  overflow: visible; /* <-- importante per l’alone */
  cursor: pointer;

  &:focus {
    outline: none;
  }

  /* Track nativo trasparente (lo fa il wrapper) */
  &::-webkit-slider-runnable-track {
    height: var(--track-height);
    background: transparent;
    border-radius: 9999px;
  }
  &::-moz-range-track {
    height: var(--track-height);
    background: transparent;
    border-radius: 9999px;
  }
  &::-ms-track {
    height: var(--track-height);
    border: 0;
    background: transparent;
    color: transparent;
  }

  /* Thumb centrato dinamicamente */
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: var(--thumb-size);
    height: var(--thumb-size);
    background: var(--thumb-color);
    border-radius: 50%;
    border: none;
    margin-top: calc((var(--thumb-size) - var(--track-height)) / -2);
    transition: box-shadow 150ms;
  }
  &::-moz-range-thumb,
  &::-ms-thumb {
    width: var(--thumb-size);
    height: var(--thumb-size);
    background: var(--thumb-color);
    border-radius: 50%;
    border: none;
    transition: box-shadow 150ms;
  }

  /* Hover / Active – come ti funziona già */
  &:active:not([disabled])::-webkit-slider-thumb {
    box-shadow: 0 0 0 6px var(--thumb-shadow);
  }
  &:hover:not(:active):not([disabled])::-webkit-slider-thumb {
    box-shadow: 0 0 0 10px var(--thumb-shadow);
  }
  &:active:not([disabled])::-moz-range-thumb {
    box-shadow: 0 0 0 6px var(--thumb-shadow);
  }
  &:hover:not(:active):not([disabled])::-moz-range-thumb {
    box-shadow: 0 0 0 10px var(--thumb-shadow);
  }
  &:active:not([disabled])::-ms-thumb {
    box-shadow: 0 0 0 6px var(--thumb-shadow);
  }
  &:hover:not(:active):not([disabled])::-ms-thumb {
    box-shadow: 0 0 0 10px var(--thumb-shadow);
  }
` as FC<Props>;
